import { useUserEventsByTypesStream } from '@/api';
import { PulseOxygenIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { unitTitleData } from '@/Components/Wellness/WellnessUtils';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { find, get } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  formatTime,
  getDateLabel,
  getRecentActivityDayWindow,
  HistoryModalTable,
  LoadingCard,
} from '../RecentActivityUtils';

const renderCells = (entry) => (
  <>
    <div className="row-cell bp-cell">{get(entry, 'spo2', '-')}</div>
    <div className="row-cell bp-cell">{get(entry, 'pulse_rate', '-')}</div>
  </>
);

const ModalContent = ({ useruuid, eventType, ...displayProps }) => {
  const trans = useSelector(translator);
  const history = useUserEventsByTypesStream({
    useruuid,
    eventTypes: eventType,
    ...getRecentActivityDayWindow(),
  });

  return (
    <HistoryModalTable
      {...displayProps}
      {...history}
      headers={[trans('DATE_L'), trans('SPO2'), trans('PULSE_BEATS')]}
      renderCells={renderCells}
    />
  );
};

ModalContent.propTypes = {
  useruuid: PropTypes.string,
  eventType: PropTypes.string,
};

export const BiometricPulseOximeterCard = (props) => {
  const {
    useruuid,
    data,
    isLoading,
    timezone,
    is24HourFormat,
    locale,
    measurementSystem,
    // trendData,
  } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  const openHistoryModal = () =>
    openDashboardModal({
      title: (
        <div className="recent-activity-modal-title">
          <PulseOxygenIcon
            className="recent-activity-modal-title-icon"
            hideEclipse={true}
            viewBox="15 15 30 30"
            fill="#005D98"
          />
          <span>{trans('PULSEOXIMETER')}</span>
        </div>
      ),
      content: (
        <ModalContent
          useruuid={useruuid}
          eventType={get(data, 'eventName')}
          timezone={timezone}
          is24HourFormat={is24HourFormat}
          locale={locale}
        />
      ),
    });

  const handleCardClick = (e) => {
    e.preventDefault();
    openHistoryModal();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openHistoryModal();
    }
  };

  if (isLoading) return <LoadingCard />;

  const timestamp = get(data, 'utc_timestamp_inms', '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const dataArray = get(data, 'data', []);
  const spo2DataKey = get(unitTitleData, [measurementSystem, 'spo2'], '');
  const pulseDataKey = get(unitTitleData, [measurementSystem, 'pulse'], '');
  const spo2 = get(find(dataArray, ['display_label', spo2DataKey]), 'display_value', '');
  const pulse = get(find(dataArray, ['display_label', pulseDataKey]), 'display_value', '');

  // const trendPercent = get(trendData, 'trendPercent', null);
  // const isUp = trendPercent > 0;
  // const isDown = trendPercent < 0;
  // const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <PulseOxygenIcon
        className="recent-activity-card-icon"
        hideEclipse={true}
        viewBox="15 15 30 30"
        fill="#005D98"
      />
      <h4 className="recent-activity-card-title">{trans('PULSEOXIMETER')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        <div className="recent-activity-data">
          <p className="recent-activity-data-value">{spo2}</p>
          <p className="recent-activity-data-label">{spo2DataKey}</p>
        </div>
        <div className="recent-activity-data">
          <p className="recent-activity-data-value">{pulse}</p>
          <p className="recent-activity-data-label">{pulseDataKey}</p>
        </div>
      </div>
      {/* <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('READINGS_TAKEN')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </> */}
    </Card>
  );
};

BiometricPulseOximeterCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
  trendIsLoading: PropTypes.bool,
};
